import { ChevronLeft, ChevronRight, LoaderCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { AdminPqrListItem } from "@/features/admin/admin.types";
import { getPqrCategoryTranslationKey } from "@/features/pqr/pqrMappers";
import { StatusBadge } from "@/features/pqr/components/StatusBadge";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

type AdminPqrTableProps = {
  activeRequestId: string | null;
  isLoading?: boolean;
  items: AdminPqrListItem[];
  onOpenRequest: (requestId: string) => void;
  onPageChange: (page: number) => void;
  page: number;
  pageCount: number;
  totalCount: number;
};

export function AdminPqrTable({
  activeRequestId,
  isLoading = false,
  items,
  onOpenRequest,
  onPageChange,
  page,
  pageCount,
  totalCount,
}: AdminPqrTableProps) {
  const { i18n, t } = useTranslation();
  const dateFormatter = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  return (
    <section className="space-y-4 rounded-[1.6rem] border border-white/10 bg-black/20 p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#b69d7e]">
            {t("pqr.admin.listTitle")}
          </p>
          <p className="text-sm text-[#d8c8b3]/68">
            {t("pqr.admin.pageSummary", { page, pageCount, totalCount })}
          </p>
        </div>
        {isLoading ? (
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#d8c8b3]/58">
            <LoaderCircle className="size-4 animate-spin" />
            {t("pqr.admin.loadingQueue")}
          </div>
        ) : null}
      </div>

      {items.length ? (
        <div className="overflow-hidden rounded-[1.45rem] border border-white/8">
          <div className="grid grid-cols-[128px_minmax(0,1.1fr)_136px_minmax(0,1fr)_120px_144px] gap-3 border-b border-white/8 bg-white/[0.03] px-5 py-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#b69d7e]">
            <span>{t("pqr.admin.columns.ticket")}</span>
            <span>{t("pqr.admin.columns.requester")}</span>
            <span>{t("pqr.admin.columns.category")}</span>
            <span>{t("pqr.admin.columns.subject")}</span>
            <span>{t("pqr.admin.columns.status")}</span>
            <span>{t("pqr.admin.columns.submittedAt")}</span>
          </div>

          <div className="divide-y divide-white/6">
            {items.map((item) => (
              <button
                className={cn(
                  "grid w-full grid-cols-[128px_minmax(0,1.1fr)_136px_minmax(0,1fr)_120px_144px] gap-3 px-5 py-4 text-left transition",
                  item.id === activeRequestId ? "bg-[#f0d2ae]/12" : "hover:bg-white/[0.045]",
                )}
                key={item.id}
                type="button"
                onClick={() => onOpenRequest(item.id)}
              >
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#d2b793]">
                  {item.ticketRef}
                </div>
                <div className="min-w-0 space-y-1">
                  <p className="truncate text-sm font-semibold text-[#fff7ef]">{item.name}</p>
                  <p className="truncate text-xs text-[#d8c8b3]/65">{item.email}</p>
                  <Badge className="w-fit" variant="outline">
                    {item.userId ? t("pqr.admin.accountLinked") : t("pqr.admin.guest")}
                  </Badge>
                </div>
                <div>
                  <Badge variant="outline">{t(getPqrCategoryTranslationKey(item.category))}</Badge>
                </div>
                <p className="line-clamp-2 text-sm text-[#d8c8b3]/78">{item.subject}</p>
                <div>
                  <StatusBadge status={item.status} />
                </div>
                <div className="text-sm text-[#d8c8b3]/65">
                  {dateFormatter.format(new Date(item.createdAt))}
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-[1.45rem] border border-dashed border-white/10 bg-white/[0.02] px-5 py-10 text-sm text-[#d8c8b3]/68">
          {t("pqr.admin.emptyBody")}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-white/8 pt-4">
        <Button
          disabled={page <= 1}
          type="button"
          variant="outline"
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="size-4" />
          {t("adminWorkspace.common.previous")}
        </Button>
        <p className="text-xs uppercase tracking-[0.18em] text-[#d8c8b3]/58">
          {page} / {pageCount}
        </p>
        <Button
          disabled={page >= pageCount}
          type="button"
          variant="outline"
          onClick={() => onPageChange(page + 1)}
        >
          {t("adminWorkspace.common.next")}
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </section>
  );
}
